/**
 * UI Utilities
 * Common UI helper functions
 */

/**
 * Wire "click on the backdrop (outside the dialog) closes the modal".
 * The handler fires only when the backdrop element itself is the click target.
 */
export const closeOnBackdropClick = (backdrop: HTMLElement, onClose: () => void): void => {
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) onClose();
  });
};

/**
 * A modern, lightweight loading indicator: a hardware-accelerated (transform-only)
 * dual-ring spinner built from Tailwind utilities — a faint brand-lime track with a
 * solid lime arc that rotates via `animate-spin`. On the Stadium Daylight skin the arc
 * flips to navy ink so it holds contrast on the concrete ground (lime reads too faint
 * on light). Use for full-panel/page loading states in place of a static icon.
 */
export const renderLoadingState = (message: string = "Loading…"): string => `
  <div class="flex flex-col items-center justify-center gap-4 p-12" role="status" aria-live="polite">
    <span class="w-12 h-12 rounded-full border-4 border-primary/25 border-t-primary daylight:border-day-ink/15 daylight:border-t-day-ink animate-spin" aria-hidden="true"></span>
    <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft font-medium">${message}</p>
  </div>
`;

/**
 * Show a notification message
 */
export const showNotification = (message: string, type: "success" | "error" | "info" = "info") => {
  // Create notification element
  const notification = document.createElement("div");
  notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 font-semibold transition-all duration-300 ${
    type === "success"
      ? "bg-green-500 text-white"
      : type === "error"
        ? "bg-red-500 text-white"
        : "bg-blue-500 text-white"
  }`;
  notification.textContent = message;

  document.body.appendChild(notification);

  // Auto-remove after 3 seconds
  setTimeout(() => {
    notification.style.opacity = "0";
    setTimeout(() => {
      notification.remove();
    }, 300);
  }, 3000);
};

/**
 * Escape a string for safe interpolation into an innerHTML template. Use for any
 * server/user-controlled value (e.g. a league name) rendered via template
 * strings, where the browser would otherwise parse embedded markup.
 */
export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });

/**
 * Build the public, no-login share URL for a league from its sharing token.
 * Hash-based to match the SPA router: <origin><path>#/leagues/<token>.
 */
export const buildShareUrl = (sharingToken: string): string =>
  `${window.location.origin}${window.location.pathname}#/leagues/${sharingToken}`;

/**
 * Copy text to the clipboard, falling back to a hidden-textarea + execCommand
 * on browsers without the async Clipboard API. Resolves true on success.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    return false;
  }
};

/**
 * Toggle a `<meta name="robots" content="noindex, nofollow">` tag on the page.
 * Used to keep the public, token-shared league view out of search engines while
 * leaving the rest of the (marketing) app indexable. Idempotent.
 */
export const setRobotsNoindex = (enabled: boolean): void => {
  const existing = document.head.querySelector<HTMLMetaElement>('meta#robots-noindex');
  if (enabled) {
    if (existing) return;
    const meta = document.createElement("meta");
    meta.id = "robots-noindex";
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
  } else {
    existing?.remove();
  }
};

/**
 * Format a date to readable string
 */
export const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Format a number to ELO rating
 */
export const formatElo = (elo: number): string => {
  return elo.toFixed(0);
};

/**
 * Calculate win percentage
 */
export const calculateWinPercentage = (wins: number, losses: number): string => {
  const total = wins + losses;
  if (total === 0) return "0.0%";
  return ((wins / total) * 100).toFixed(1) + "%";
};
